import type {
    BausteinFormat,
    CurriculumItemReason,
    EvidenceInstrument,
    Modality,
    RequirementOrigin,
    SkillLevel,
    SkillSource,
} from "./types"

/**
 * Every rule the composer applies, as data.
 *
 * Until now each of them was a literal buried in `compose.ts` or `gap.ts` —
 * `originWeight = {regulatorik: 100, ...}`, the value-per-hour denominator, the
 * "too basic" tolerance, the never-drop rule for duties. They were invisible to
 * the people whose policy they encode. This file makes them a first-class,
 * inspectable, tunable object, which is what the HR Steuerung page edits.
 *
 * TWO INVARIANTS, both load-bearing:
 *
 * 1. `DEFAULT_POLICY` reproduces the pre-parameterised engine BYTE FOR BYTE.
 *    Every default below is a provable no-op, not an approximation — see the
 *    per-field comments. The golden fixtures assert it.
 *
 * 2. **No arrays, anywhere.** Sets are Records keyed by their union type. That
 *    makes the policy order-free, so merging is trivial, diffing is a field
 *    walk, JSON is canonical, and "how many settings differ from standard?"
 *    is answerable without a deep-equality library.
 *
 * Still framework-free: this file imports nothing but its own types. Zod
 * validation for the API boundary lives in `modules/lernpfad/schemas/`, not
 * here, so the engine stays liftable onto another runtime as-is.
 */

// ---------------------------------------------------------------------------
// The policy
// ---------------------------------------------------------------------------

export interface ResolvedPolicy {
    /** Bumped when a field's MEANING changes, so stored settings can migrate. */
    version: 1

    targetProfile: {
        /** A regulatory origin survives a collision with an aspiration. */
        regulatorikWins: boolean
    }

    sources: {
        /** Per-source credibility, 0–1. Replaces the old on/off-only model. */
        trust: Record<SkillSource, number>
        /** A reading is admitted only if its source reaches this trust. */
        minTrust: number
        /** `gate`: trust admits or rejects. `discount`: also caps the level read. */
        mode: "gate" | "discount"
    }

    evidence: {
        /** Minimum instrument that counts as proof. The Kirkpatrick↔LTEM knob. */
        floor: EvidenceInstrument
        /** Fallback when a Baustein declares no instrument of its own. */
        instrumentByFormat: Record<BausteinFormat, EvidenceInstrument | null>
        defaultInstrument: EvidenceInstrument
        /** Gate the skill credit a completion grants. */
        gateSkillCredit: boolean
        /** Gate whether a completion discharges a regulatory duty (FIDLEG Art. 6). */
        gateObligationDischarge: boolean
        /** Require a confirmed L3 transfer before the level rises. */
        requireBehaviour: boolean
        /** L1 target on "likelihood to apply", 1–5. */
        reactionTarget: number
        /** L2 pass mark as a scaled score, 0–1. */
        learningPassScaled: number
        /** Days after completion when the L3 confirmation window opens / closes. */
        behaviourWindowDays: { from: number; to: number }
        /** Days after completion when Level 4 results are read. */
        resultsWindowDays: { from: number; to: number }
    }

    gaps: {
        originRank: Record<RequirementOrigin, number>
        /** Smallest `required − current` worth acting on. */
        minSize: number
        /** A Baustein may not be credited with more than this lift in one step. */
        maxLevelJump: number
        /** A Baustein counts as closing a gap only if it lifts by at least this. */
        minLift: number
    }

    obligations: {
        /** Duties are placed even over budget. The invariant the engine exists for. */
        neverDrop: boolean
        /** Global multiplier on each obligation's own lead time. */
        leadTimeMultiplier: number
    }

    ranking: {
        originWeight: Record<RequirementOrigin, number>
        /** Value per hour, per dollar, or raw value. Phillips ROI selects `chf`. */
        denominator: "hours" | "usd" | "flat"
        /** Floor on the hours denominator. */
        minHours: number
        /** Floor on the USD denominator — guards 0/0 on a zero-cost format. */
        minUsd: number
        /** Discard when `maxHeld − baustein.level` exceeds this. */
        tooBasicTolerance: number
        /** Display order within a quarter. */
        reasonRank: Record<CurriculumItemReason, number>
    }

    cost: {
        perHourUsdByFormat: Record<BausteinFormat, number>
        fixedUsdByFormat: Record<BausteinFormat, number>
        /** Loaded salary cost of the learner's time. 0 = ignore. */
        opportunityUsdPerHour: number
        /** A Baustein's own `costUsd`, when present, replaces the computed one. */
        honourCatalogOverride: boolean
    }

    budget: {
        /** Scale the quarter budget by Pensum. The demo's most persuasive lever. */
        scaleByPensum: boolean
        /** Length of one planning period in months. 3 = quarters. */
        periodMonths: number
    }

    scheduling: {
        maxItemsPerPeriod: number
        maxHoursPerItem: number
        /** Interleaving limit: distinct skill categories per period. */
        maxCategoriesPerPeriod: number
        deferralMode: "chain-vs-remaining" | "period-only"
        prerequisites: { expand: boolean; maxDepth: number }
    }

    /** Spaced repetition — Ebbinghaus. */
    reinforcement: {
        enabled: boolean
        afterDays: number
        durationHours: number
        appliesTo: Record<CurriculumItemReason, boolean>
    }

    /** Modality mix — 70-20-10. */
    mix: {
        enabled: boolean
        modalityOf: Record<BausteinFormat, Modality>
        /** Target share per modality, as percentages summing to 100. */
        target: Record<Modality, number>
        /** Score multiplier applied to an under-represented modality. */
        strength: number
    }
}

/** What callers pass: every field optional at every depth. */
export type ComposerPolicy = DeepPartial<ResolvedPolicy>

export type DeepPartial<T> = T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T

// ---------------------------------------------------------------------------
// Defaults — each one a provable no-op
// ---------------------------------------------------------------------------

export const DEFAULT_POLICY: ResolvedPolicy = Object.freeze({
    version: 1,

    targetProfile: { regulatorikWins: true },

    sources: {
        // All 1 against a 0.5 threshold ⇒ every allowed source is admitted, and
        // `consider()` reduces to today's "highest level wins".
        trust: {
            stellenprofil: 1,
            lernhistorie: 1,
            cv: 1,
            mag: 1,
            selbsteinschaetzung: 1,
        },
        minTrust: 0.5,
        mode: "gate",
    },

    evidence: {
        // `abschluss` is rank 0, so `rank >= floor` is always true: completions
        // credit exactly as unconditionally as they do today.
        floor: "abschluss",
        instrumentByFormat: {
            "e-learning": null,
            praesenz: null,
            webinar: null,
            selbststudium: null,
            "on-the-job": null,
            social: null,
        },
        defaultInstrument: "abschluss",
        gateSkillCredit: false,
        gateObligationDischarge: false,
        requireBehaviour: false,
        // Thresholds from the engagement template. Inert until a gate is on.
        reactionTarget: 4,
        learningPassScaled: 0.66,
        behaviourWindowDays: { from: 14, to: 30 },
        resultsWindowDays: { from: 60, to: 90 },
    },

    gaps: {
        originRank: { regulatorik: 0, rolle: 1, mag: 2, ziel: 3 },
        // Levels are integers, so `size < 1` ⇔ `current >= required`.
        minSize: 1,
        // The largest possible jump is 4 − 0, so `> 4` never fires.
        maxLevelJump: 4,
        // Integers again: `lift < 1` ⇔ `taught.level <= gap.current`.
        minLift: 1,
    },

    obligations: { neverDrop: true, leadTimeMultiplier: 1 },

    ranking: {
        originWeight: { regulatorik: 100, rolle: 40, mag: 25, ziel: 15 },
        denominator: "hours",
        minHours: 0.5,
        minUsd: 1,
        // `maxHeld - level > 1` ⇔ `level + 1 < maxHeld`.
        tooBasicTolerance: 1,
        reasonRank: { pflicht: 0, voraussetzung: 1, luecke: 2, ziel: 3, auffrischung: 4 },
    },

    cost: {
        // A rate card, not 51 invented prices. "Why is this course exactly
        // USD 1,340?" has no good answer; "we price Präsenz at USD 150/h plus
        // USD 100 fixed" has an excellent one — and it makes the price a lever
        // HR can move rather than a number buried in a data file.
        perHourUsdByFormat: {
            praesenz: 150,
            webinar: 60,
            "e-learning": 25,
            selbststudium: 0,
            "on-the-job": 0,
            social: 0,
        },
        fixedUsdByFormat: {
            praesenz: 100, // room, catering, travel
            webinar: 0,
            "e-learning": 0,
            selbststudium: 0,
            "on-the-job": 0,
            social: 0,
        },
        opportunityUsdPerHour: 0,
        honourCatalogOverride: true,
    },

    budget: { scaleByPensum: true, periodMonths: 3 },

    scheduling: {
        // Finite, never Infinity: JSON.stringify(Infinity) is `null`, and an
        // "unbounded" default that does not survive the wire is not a default.
        // The catalog has 51 Bausteine, a 16 h maximum, and 5 skill categories.
        maxItemsPerPeriod: 999,
        maxHoursPerItem: 999,
        maxCategoriesPerPeriod: 9,
        deferralMode: "chain-vs-remaining",
        prerequisites: { expand: true, maxDepth: 8 },
    },

    reinforcement: {
        enabled: false,
        afterDays: 30,
        durationHours: 1,
        appliesTo: {
            pflicht: true,
            voraussetzung: false,
            luecke: false,
            ziel: false,
            auffrischung: false,
        },
    },

    mix: {
        enabled: false,
        modalityOf: {
            "on-the-job": "on-the-job",
            social: "social",
            praesenz: "formal",
            webinar: "formal",
            "e-learning": "formal",
            selbststudium: "formal",
        },
        target: { "on-the-job": 70, social: 20, formal: 10 },
        strength: 1.5,
    },
} satisfies ResolvedPolicy)

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

const RESOLVED = new WeakMap<object, ResolvedPolicy>()

/**
 * Merge a partial policy onto the defaults.
 *
 * Two rules that look like fussiness and are not:
 *
 * - **`undefined` is skipped, not assigned.** `{...defaults, ...{regulatorik:
 *   undefined}}` yields `undefined * gap.size === NaN`, and a NaN comparator is
 *   treated as 0 by V8 — the sort order silently becomes an artefact of the
 *   array length. `JSON.parse` never produces an explicit `undefined`; React
 *   state from a half-filled form does, and the client path has no Zod.
 * - **A non-finite number falls back to the default.** Same reason, plus
 *   `Infinity` does not survive `JSON.stringify`, so a client and a server that
 *   disagreed about it would render contradictory numbers on the same stage.
 */
function merge<T>(base: T, patch: unknown): T {
    if (patch === undefined || patch === null) return base
    if (typeof base === "number") {
        return typeof patch === "number" && Number.isFinite(patch) ? (patch as T) : base
    }
    if (typeof base !== "object" || base === null) {
        return typeof patch === typeof base ? (patch as T) : base
    }
    if (typeof patch !== "object") return base

    const result = { ...(base as Record<string, unknown>) }
    for (const [key, value] of Object.entries(patch as Record<string, unknown>)) {
        if (value === undefined) continue
        if (!(key in result)) continue // unknown key: ignore rather than smuggle
        result[key] = merge(result[key], value)
    }
    return result as T
}

const bound = (value: number, low: number, high: number) =>
    Math.min(high, Math.max(low, value))

/**
 * Semantic ranges, applied after merge.
 *
 * This runs in the ENGINE, not only in the Zod schema, because
 * `use-lernpfad-demo.ts` calls `composeCurriculum` straight from React state —
 * only the population summary crosses the API boundary. Validation that lives
 * only in the route protects one of two consumers.
 */
function clamp(policy: ResolvedPolicy): ResolvedPolicy {
    const trust = { ...policy.sources.trust }
    for (const key of Object.keys(trust) as SkillSource[]) {
        trust[key] = bound(trust[key], 0, 1)
    }
    const originWeight = { ...policy.ranking.originWeight }
    for (const key of Object.keys(originWeight) as RequirementOrigin[]) {
        originWeight[key] = Math.max(0, originWeight[key])
    }

    return {
        ...policy,
        sources: { ...policy.sources, trust, minTrust: bound(policy.sources.minTrust, 0, 1) },
        evidence: {
            ...policy.evidence,
            reactionTarget: bound(policy.evidence.reactionTarget, 1, 5),
            learningPassScaled: bound(policy.evidence.learningPassScaled, 0, 1),
        },
        gaps: {
            ...policy.gaps,
            minSize: bound(policy.gaps.minSize, 0, 4),
            maxLevelJump: bound(policy.gaps.maxLevelJump, 1, 4),
            minLift: bound(policy.gaps.minLift, 0, 4),
        },
        obligations: {
            ...policy.obligations,
            leadTimeMultiplier: bound(policy.obligations.leadTimeMultiplier, 0, 10),
        },
        ranking: {
            ...policy.ranking,
            originWeight,
            // Both floors must stay strictly positive: they are the only thing
            // standing between a zero-duration or zero-cost Baustein and a NaN.
            minHours: Math.max(0.1, policy.ranking.minHours),
            minUsd: Math.max(1, policy.ranking.minUsd),
            tooBasicTolerance: bound(policy.ranking.tooBasicTolerance, 0, 4),
        },
        budget: { ...policy.budget, periodMonths: bound(policy.budget.periodMonths, 1, 12) },
        scheduling: {
            ...policy.scheduling,
            maxItemsPerPeriod: Math.max(1, policy.scheduling.maxItemsPerPeriod),
            maxHoursPerItem: Math.max(0.5, policy.scheduling.maxHoursPerItem),
            maxCategoriesPerPeriod: Math.max(1, policy.scheduling.maxCategoriesPerPeriod),
            prerequisites: {
                ...policy.scheduling.prerequisites,
                maxDepth: bound(policy.scheduling.prerequisites.maxDepth, 0, 16),
            },
        },
        reinforcement: {
            ...policy.reinforcement,
            afterDays: bound(policy.reinforcement.afterDays, 1, 365),
            durationHours: bound(policy.reinforcement.durationHours, 0.25, 8),
        },
        mix: { ...policy.mix, strength: bound(policy.mix.strength, 1, 5) },
    }
}

/**
 * Resolve a caller's partial policy against the defaults.
 *
 * Memoised on the caller's own object identity, which matters: `resolvePolicy`
 * is called at four entry points, and `summarisePopulation` reaches those ~6,000
 * times per request over the 1,960-person roster. A WeakMap makes all but the
 * first free and keeps nothing alive that the caller has dropped.
 *
 * The `undefined` fast path returns `DEFAULT_POLICY` itself, so "reset to
 * standard" should set the policy to `undefined` rather than to a structural
 * copy of the defaults — identity is what keeps that path free.
 */
export function resolvePolicy(input?: ComposerPolicy): ResolvedPolicy {
    if (input === undefined) return DEFAULT_POLICY
    const cached = RESOLVED.get(input)
    if (cached) return cached
    const resolved = clamp(merge(DEFAULT_POLICY, input))
    RESOLVED.set(input, resolved)
    return resolved
}

// ---------------------------------------------------------------------------
// Small shared guards
// ---------------------------------------------------------------------------

/** Force a value onto the 0–4 skill scale as an integer. */
export function clampLevel(value: number): SkillLevel {
    return Math.max(0, Math.min(4, Math.trunc(value))) as SkillLevel
}

/** Replace NaN / ±Infinity with a fallback before it reaches a comparator. */
export function finiteOr(value: number, fallback: number): number {
    return Number.isFinite(value) ? value : fallback
}

/**
 * How many leaf fields differ from the standard — the Steuerung page's
 * "n Parameter weichen vom Standard ab" badge. Cheap precisely because the
 * policy contains no arrays.
 */
export function countOverrides(policy: ResolvedPolicy): number {
    let count = 0
    const walk = (base: unknown, current: unknown) => {
        if (base !== null && typeof base === "object") {
            for (const key of Object.keys(base as Record<string, unknown>)) {
                walk(
                    (base as Record<string, unknown>)[key],
                    (current as Record<string, unknown>)[key],
                )
            }
            return
        }
        if (base !== current) count += 1
    }
    walk(DEFAULT_POLICY, policy)
    return count
}
